import { makeVar, ReactiveVar, useReactiveVar } from '@apollo/client'
import React from 'react'
import secureLocalStorage from 'react-secure-storage'

import { FeatureType } from '../../configuration/constants/enums'
import { useFeatureFlag } from '../../configuration/hooks/useFeatureFlag'
import { useUser } from '../../configuration/hooks/useUser'
import { LocalStorageSensitivity } from '../constants/enums'
import { useReportError } from '../helpers/reportError'
import useSavingIndicator, { SAVING_STATE } from './useSavingIndicator'

const localItems = {}

/* This hook provides a way to read and store a value globally on the client
   under a string `key`.

   If the value is PHI, then we may persist the value to LocalStorage if the
   current user is a provider and they have the PROVIDER_LOCAL_STORAGE feature
   flag enabled. The localstorage (and in-memory) value will be cleared on any
   significant authentication event.

   If the value is NON_PHI, then we freely persist it to LocalStorage and do not
   clear it.

   Whether persisted or not, the `value` and `setValue` functions returned by
   the hook synchronize the value across components, just like the Apollo
   ReactiveVar hook it uses under-the-hood.

   Example:

   function MyComponent({}) {
     const { value, setValue } = useLocalState<string>(
       'allergies',
       LocalStorageSensitivity.PHI)

     return (
       <div>
         <h1>Allergies: {value}</h1>
         <button onClick={() => setValue('tree nuts')}>Tree Nuts</button>
       </div>
     )
   }
*/
function useLocalState<T>(key: string, sensitivity: LocalStorageSensitivity) {
  const { isProvider } = useUser()
  const providerLocalStorageFeatureFlag = useFeatureFlag(
    FeatureType.PROVIDER_LOCAL_STORAGE,
  )
  const isPHI = sensitivity === LocalStorageSensitivity.PHI
  const isLocalStorageEnabled =
    !isPHI || (isProvider && providerLocalStorageFeatureFlag)
  const storageKey = getStorageKey(key, sensitivity)
  const reportError = useReportError()

  const needToHydrateAfterConfigurationLoads = React.useRef(
    // Hydrate if initially false - potentially meaning config api is still loading
    !providerLocalStorageFeatureFlag,
  )

  const reactiveVar = getLocalItem<T>(
    reportError,
    storageKey,
    isLocalStorageEnabled,
  )
  const { value, savingText } = useReactiveVar<LocalItem<T>>(reactiveVar)

  const valueReactiveVar = React.useCallback(
    () => reactiveVar().value,
    [reactiveVar],
  )

  const updateLocalItem = React.useCallback(
    (newValue: Partial<LocalItem<T>>) => {
      reactiveVar({
        ...reactiveVar(),
        ...newValue,
      })
    },
    [reactiveVar],
  )

  const handleUpdateSavingText = React.useCallback(
    (savingText: SAVING_STATE | null) => {
      updateLocalItem({
        savingText,
      })
    },
    [updateLocalItem],
  )

  const inidicateSaving = useSavingIndicator(handleUpdateSavingText)

  const setValue = React.useCallback(
    (newValue: T | null, bypassSavingIndicator: boolean = false) => {
      if (isLocalStorageEnabled) {
        if (!bypassSavingIndicator) {
          inidicateSaving()
        }

        if (newValue) {
          if (isPHI) {
            secureLocalStorage.setItem(storageKey, JSON.stringify(newValue))
          } else {
            window.localStorage.setItem(storageKey, JSON.stringify(newValue))
          }
        } else {
          if (isPHI) {
            secureLocalStorage.removeItem(storageKey)
          } else {
            window.localStorage.removeItem(storageKey)
          }
        }
      }

      updateLocalItem({
        value: newValue,
      })
    },
    [
      inidicateSaving,
      isLocalStorageEnabled,
      isPHI,
      storageKey,
      updateLocalItem,
    ],
  )

  // Hydrate the value from local storage if the feature flag value changes from false -> true.
  // NOTE: Mainly used to rehydrate after configuration api has loaded. This handles the race condition
  // that `useLocalState` is used before the configuration API has loaded the
  // PROVIDER_LOCAL_STORAGE feature flag.
  React.useEffect(() => {
    if (
      !needToHydrateAfterConfigurationLoads.current ||
      !providerLocalStorageFeatureFlag
    ) {
      return
    }

    const initialValue = getInitialValue<T>(
      reportError,
      storageKey,
      isLocalStorageEnabled,
    )
    if (!initialValue) {
      return
    }

    // Update reactiveVar directly instead of `setValue` to bypass saving indicator since hydrating
    updateLocalItem({
      value: initialValue,
    })
    needToHydrateAfterConfigurationLoads.current = false // only hydrate once
  }, [
    isLocalStorageEnabled,
    needToHydrateAfterConfigurationLoads,
    providerLocalStorageFeatureFlag,
    reportError,
    storageKey,
    updateLocalItem,
  ])

  return {
    savingText,
    valueReactiveVar,
    value,
    setValue,
  }
}

interface LocalItem<T> {
  value: T | null
  savingText: SAVING_STATE | null
}

export const getStorageKey = (
  key: string,
  sensitivity: LocalStorageSensitivity = LocalStorageSensitivity.NON_PHI,
) =>
  sensitivity === LocalStorageSensitivity.PHI ? `cmd-phi-${key}` : `cmd-${key}`

const getInitialValue = <T>(
  reportError: any,
  storageKey: string,
  isLocalStorageEnabled: boolean,
) => {
  const lsVal = isLocalStorageEnabled
    ? secureLocalStorage.getItem(storageKey) ??
      window.localStorage.getItem(storageKey)
    : null
  let initialValue: T | null = null

  if (lsVal && typeof lsVal === 'string') {
    try {
      initialValue = JSON.parse(lsVal) as T
    } catch (error) {
      reportError(error, `Couldn't read from local storage: ${error.message}`, {
        storageKey,
        unparsedValue: lsVal,
      })
      window.localStorage.removeItem(storageKey)
    }
  }

  return initialValue
}

export function getLocalItem<T>(
  reportError: any,
  storageKey: string,
  isLocalStorageEnabled: boolean,
): ReactiveVar<LocalItem<T>> {
  if (localItems[storageKey]) {
    return localItems[storageKey]
  }

  const initialValue = getInitialValue<T>(
    reportError,
    storageKey,
    isLocalStorageEnabled,
  )
  const localItem: LocalItem<T> = {
    value: initialValue,
    savingText: null,
  }

  localItems[storageKey] = makeVar(localItem)
  return localItems[storageKey]
}

// this function ensures that we are clearing out things via clearLocalPHI
// it pulls out the original storageKey from the secure key prefix.
// the @secure. prefix is from the library's default:
// https://github.com/sushinpv/react-secure-storage/blob/master/src/lib/utils.ts#L7
// the `s.` is due to the fact that we are storing everything as a string and
// `s` is the library's default for a string value with this gnarly ternary:
// https://github.com/sushinpv/react-secure-storage/blob/master/src/lib/index.ts#L14
const getNormalKeyFromSecureKey = (key: string) => key.replace('@secure.s.', '')

export function clearLocalPHI() {
  Object.keys(window.localStorage)
    .filter((key) => key.indexOf('cmd-phi-') > -1)
    .forEach((key) => {
      secureLocalStorage.removeItem(getNormalKeyFromSecureKey(key))
    })

  Object.keys(localItems)
    .filter((key) => key.indexOf('cmd-phi-') > -1)
    .forEach((key) => {
      // Also clear reactive vars from `localItems`
      localItems[getNormalKeyFromSecureKey(key)]({
        value: null,
        savingText: null,
      })
    })
}

// Tests require an empty `localItems` object.
export function __resetLocalItemsForTests() {
  Object.keys(localItems).forEach((key) => {
    delete localItems[key]
  })
}

export default useLocalState
